import React, {useEffect, useRef, useState} from "react";
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import {
  Add20Regular,
  ArrowLeft20Regular,
  Delete20Regular,
  MoreHorizontal20Regular,
  Rename20Regular,
} from "@fluentui/react-icons";
import {useAppState} from "../context/AppContext";
import {MAX_THREADS, ChatThread} from "../../services/chatStore";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 8px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerTitle: {
    flex: 1,
    fontWeight: 600,
    fontSize: "13px",
  },
  filters: {
    display: "flex",
    gap: "4px",
    padding: "8px",
  },
  list: {
    flex: 1,
    overflowY: "auto",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "8px 8px 8px 12px",
    cursor: "pointer",
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    ":hover": {backgroundColor: tokens.colorNeutralBackground1Hover},
  },
  rowActive: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  rowText: {
    flex: 1,
    minWidth: 0,
  },
  rowTitle: {
    display: "block",
    fontSize: "13px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowMeta: {
    display: "block",
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  empty: {
    padding: "24px 16px",
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
  },
  footer: {
    padding: "8px 12px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
  },
});

/** Coarse relative time — enough to tell yesterday's chat from last week's. */
function relativeTime(timestamp: number, now: number): string {
  const minutes = Math.round((now - timestamp) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

export function ThreadList() {
  const styles = useStyles();
  const {
    threads,
    activeThreadId,
    workbookName,
    openThread,
    removeThread,
    retitleThread,
    newChat,
    setView,
  } = useAppState();

  const [showAll, setShowAll] = useState(false);
  const [renaming, setRenaming] = useState<ChatThread | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleting, setDeleting] = useState<ChatThread | null>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const now = Date.now();

  const fromThisWorkbook = threads.filter(
    (t) => t.workbookName === workbookName,
  );
  const visible = showAll ? threads : fromThisWorkbook;

  useEffect(() => {
    if (renaming) renameInputRef.current?.focus();
  }, [renaming]);

  function confirmRename() {
    if (renaming && renameValue.trim()) {
      retitleThread(renaming.id, renameValue.trim());
    }
    setRenaming(null);
  }

  function confirmDelete() {
    if (deleting) removeThread(deleting.id);
    setDeleting(null);
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft20Regular />}
          onClick={() => setView("chat")}
          aria-label="Back to chat"
        />
        <span className={styles.headerTitle}>Chats</span>
        <Button
          appearance="subtle"
          icon={<Add20Regular />}
          onClick={newChat}
          aria-label="New chat"
          title="New chat"
        />
      </div>

      {threads.length > 0 && (
        <div className={styles.filters}>
          <Badge
            appearance={showAll ? "outline" : "filled"}
            color="brand"
            onClick={() => setShowAll(false)}
            style={{cursor: "pointer"}}
          >
            This workbook
          </Badge>
          <Badge
            appearance={showAll ? "filled" : "outline"}
            color="brand"
            onClick={() => setShowAll(true)}
            style={{cursor: "pointer"}}
          >
            All chats ({threads.length})
          </Badge>
        </div>
      )}

      <div className={styles.list}>
        {visible.length === 0 && (
          <div className={styles.empty}>
            <Body1>
              {threads.length === 0
                ? "No chats yet."
                : "No chats from this workbook yet."}
            </Body1>
            {!showAll && threads.length > 0 && (
              <div>
                <Button
                  appearance="transparent"
                  size="small"
                  onClick={() => setShowAll(true)}
                >
                  Show all chats
                </Button>
              </div>
            )}
          </div>
        )}
        {visible.map((thread) => (
          <div
            key={thread.id}
            className={mergeClasses(
              styles.row,
              thread.id === activeThreadId && styles.rowActive,
            )}
            onClick={() => openThread(thread.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openThread(thread.id);
              }
            }}
          >
            <div className={styles.rowText}>
              <span className={styles.rowTitle}>{thread.title}</span>
              <Caption1 className={styles.rowMeta}>
                {thread.workbookName} · {relativeTime(thread.updatedAt, now)}
              </Caption1>
            </div>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<MoreHorizontal20Regular />}
                  aria-label={`Actions for ${thread.title}`}
                  onClick={(e) => e.stopPropagation()}
                />
              </MenuTrigger>
              <MenuPopover onClick={(e) => e.stopPropagation()}>
                <MenuList>
                  <MenuItem
                    icon={<Rename20Regular />}
                    onClick={() => {
                      setRenameValue(thread.title);
                      setRenaming(thread);
                    }}
                  >
                    Rename
                  </MenuItem>
                  <MenuItem
                    icon={<Delete20Regular />}
                    onClick={() => setDeleting(thread)}
                  >
                    Delete
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </div>
        ))}
      </div>

      <div className={styles.footer}>
        <Caption1>
          Chats are stored locally on this machine — only the last {MAX_THREADS}{" "}
          are kept.
        </Caption1>
      </div>

      <Dialog open={renaming !== null}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Rename chat</DialogTitle>
            <DialogContent>
              <Input
                ref={renameInputRef}
                value={renameValue}
                onChange={(_, data) => setRenameValue(data.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") confirmRename();
                }}
                style={{width: "100%"}}
              />
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setRenaming(null)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={confirmRename}>
                Rename
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <Dialog open={deleting !== null}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete chat</DialogTitle>
            <DialogContent>
              “{deleting?.title}” will be deleted from this machine. This can't
              be undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleting(null)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={confirmDelete}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
